import { reactive } from 'vue'

export type VariableType = 'string' | 'number' | 'boolean' | 'json' | 'color' | 'vec2' | 'vec3'

export interface Variable {
  key: string
  type: VariableType
  value: any
}

const STORAGE_KEY = 'mflow_variables'

export const variablesState = reactive({
  globalVariables: [] as Variable[],
  workflowVariables: [] as Variable[]
})

function save() {
  try {
    const data = {
      globalVariables: variablesState.globalVariables,
      workflowVariables: variablesState.workflowVariables
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch (e) {
    console.warn('[variables.store] Failed to save', e)
  }
}

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const data = JSON.parse(raw)
      variablesState.globalVariables.splice(0, variablesState.globalVariables.length, ...(data.globalVariables ?? []))
      variablesState.workflowVariables.splice(0, variablesState.workflowVariables.length, ...(data.workflowVariables ?? []))
    }
  } catch (e) {
    console.warn('[variables.store] Failed to load', e)
  }
}

export function getGlobalVariables(): Variable[] {
  return variablesState.globalVariables
}

export function getWorkflowVariables(): Variable[] {
  return variablesState.workflowVariables
}

export function addGlobalVariable(v: Variable): void {
  const exists = variablesState.globalVariables.some(it => it.key === v.key)
  if (exists) throw new Error(`变量名已存在: ${v.key}`)
  variablesState.globalVariables.push(v)
  save()
}

export function addWorkflowVariable(v: Variable): void {
  const exists = variablesState.workflowVariables.some(it => it.key === v.key)
  if (exists) throw new Error(`变量名已存在: ${v.key}`)
  variablesState.workflowVariables.push(v)
  save()
}

export function updateGlobalVariable(v: Variable): void {
  const idx = variablesState.globalVariables.findIndex(it => it.key === v.key)
  if (idx >= 0) variablesState.globalVariables[idx] = v
  save()
}

export function updateWorkflowVariable(v: Variable): void {
  const idx = variablesState.workflowVariables.findIndex(it => it.key === v.key)
  if (idx >= 0) variablesState.workflowVariables[idx] = v
  save()
}

export function deleteGlobalVariable(key: string): void {
  const idx = variablesState.globalVariables.findIndex(it => it.key === key)
  if (idx >= 0) variablesState.globalVariables.splice(idx, 1)
  save()
}

export function deleteWorkflowVariable(key: string): void {
  const idx = variablesState.workflowVariables.findIndex(it => it.key === key)
  if (idx >= 0) variablesState.workflowVariables.splice(idx, 1)
  save()
}

// 新增：变量导入/导出能力
export interface VariablesSnapshot {
  globalVariables: Variable[]
  workflowVariables: Variable[]
}

export function exportVariables(): VariablesSnapshot {
  return {
    globalVariables: variablesState.globalVariables.map(v => ({ ...v })),
    workflowVariables: variablesState.workflowVariables.map(v => ({ ...v }))
  }
}

export function importVariables(snapshot: VariablesSnapshot): void {
  variablesState.globalVariables.splice(0, variablesState.globalVariables.length, ...(snapshot?.globalVariables ?? []))
  variablesState.workflowVariables.splice(0, variablesState.workflowVariables.length, ...(snapshot?.workflowVariables ?? []))
  save()
}

load()